// File: src/pages/CompleteOrder.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
  Form,
  FormGroup,
  Label,
} from "reactstrap";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import swal from "sweetalert";
import { ImDownload } from "react-icons/im";
import { FaFilter, FaPencilAlt } from "react-icons/fa";
import { Trash2 } from "react-feather";
import { AiOutlineDownload } from "react-icons/ai";

import axios from "axios";
import { pdf } from "@react-pdf/renderer";
import LatestInvoice from "./LastestInvoice";

import {
  _Get,
  _Put,
  _PutUpdate,
  _Delete,
  _DeleteAllSales,
} from "../import/ApiEndPoint/ApiCalling";
import { CheckPermission } from "../import/CheckPermission";
import { exportDataToPDF } from "../import/Downloader";
import {
  view_create_order_history,
  Delete_Sales,
  Last_Ledger_Balance,
  arnSave,
  updatedCNData,
} from "../import/ApiEndPoint/Api";
import { baseURL } from "../import/ApiEndPoint/TestApi";
import { ToWords } from "to-words";

/* ============== tiny page-scoped CSS ============== */
const injectOnce = () => {
  if (document.getElementById("completeorder-inline-css")) return;
  const style = document.createElement("style");
  style.id = "completeorder-inline-css";
  style.innerHTML = `
    .co-toolbar .count-pill{
      background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px;
    }
    .co-table-wrap{
      width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff;
    }
    .co-table{ width:100%; border-collapse:collapse; table-layout:auto; min-width:980px; }
    .co-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb; padding:6px 8px; position:sticky; top:0; z-index:1; text-align:center; white-space:nowrap;
    }
    .co-table tbody td{
      padding:8px 10px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:14px; text-align:center;
    }
    .co-table tbody tr:hover{ background:#f9fbff; }
    .co-totals{ font-weight:700; background:#fafafa; position:sticky; bottom:0; }
    .action-chips{ display:flex; justify-content:center; align-items:center; gap:8px }
    .chip{ display:inline-grid; place-items:center; width:28px; height:28px; border-radius:16px; cursor:pointer }
    .chip-edit{ background:#d46f10; }
    .chip-delete{ background:#ec1809; }
    .chip-check{ background:#475569; }
    .co-form .form-group { margin-bottom:8px; }
    .co-form label { font-size:12px; margin-bottom:4px; }
    .co-form input, .co-form select { font-size:12px; height:34px; }
  `;
  document.head.appendChild(style);
};

/* ============== helpers ============== */
const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const fmtDate = (s) => (s ? String(s).split("T")[0] : "");
const INR = (n) => (n === 0 || n ? Number(n).toFixed(2) : "");

const N = (v) => {
  const num = Number(v);
  return isNaN(num) ? 0 : num;
};
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/* ============== “Print payload” sanitizer ==============
   Fixes the common dirty data before handing to <LatestInvoice/>
   So the PDF always uses one clean object and never mixes fields.
*/
function buildPrintableInvoice(rawRow, company) {
  const r = JSON.parse(JSON.stringify(rawRow || {})); // deep-ish clone
  // 1) Prefix/Suffix into invoiceId for visual display
  const prefix = (company && company.Prefix) || "";
  const suffix = (company && company.Suffix) || "";
  if (r.invoiceId !== undefined) {
    r.invoiceId = `${prefix}${r.invoiceId || ""}${suffix}`;
  }

  // 2) Ensure party object shape (fallback from top-level fields)
  if (!r.partyId || typeof r.partyId !== "object") {
    r.partyId = {
      CompanyName: r.fullName || "",
      contactNumber: r.MobileNo || "",
      State: r.state || "",
      City: r.city || "",
      Pincode: r.pincode || "",
      address: r.ownerAddress || r.address || "",
      email: r.email || "",
      gstNumber: r.gstNumber || "",
    };
  }

  // 3) discountDetails cleanup (string amount → number; normalize type/title)
  if (Array.isArray(r.discountDetails)) {
    r.discountDetails = r.discountDetails.map((d) => ({
      ...d,
      type: toStr(d?.type).trim(), // keep original case, the PDF lowercases for compare
      title:
        d?.title ||
        (toStr(d?.type).toLowerCase() === "charges" ? "Charges" : "Discount"),
      amount: N(d?.amount ?? d?.discountedAmount ?? d?.discountedValue ?? 0),
      discountedAmount: undefined,
      discountedValue: undefined,
    }));
  } else {
    r.discountDetails = [];
  }

  // 4) gstDetails sanity (prefer these for taxes)
  const gst = Array.isArray(r.gstDetails) ? r.gstDetails : [];
  const sumGST = gst.reduce(
    (acc, row) => {
      const taxable = N(row?.taxable);
      const cgstAmt = N(row?.centralTax?.[0]?.amount);
      const sgstAmt = N(row?.stateTax?.[0]?.amount);
      const igstAmt = N(row?.igstTax?.[0]?.amount);
      return {
        taxable: acc.taxable + taxable,
        cgst: acc.cgst + cgstAmt,
        sgst: acc.sgst + sgstAmt,
        igst: acc.igst + igstAmt,
        grand: acc.grand + N(row?.grandTotal),
      };
    },
    { taxable: 0, cgst: 0, sgst: 0, igst: 0, grand: 0 },
  );

  // 5) orderItems repair (obviously wrong taxableAmount → recompute)
  if (Array.isArray(r.orderItems)) {
    r.orderItems = r.orderItems.map((it) => {
      const qty = N(it?.qty);
      const price = N(it?.price);
      const totalPrice = N(it?.totalPrice) || round2(qty * price);
      let taxableAmount = N(it?.taxableAmount);
      // If taxableAmount is absurd (e.g., 551528 for a ~27k sale), recompute from totalPrice
      if (taxableAmount === 0 || taxableAmount > totalPrice * 50) {
        taxableAmount = totalPrice;
      }
      // Normalize GST rate number
      const rate = N(it?.gstPercentage ?? it?.productId?.GSTRate);
      // If grandTotal missing, compute approximate from taxable + tax
      let grandTotal = N(it?.grandTotal);
      if (!grandTotal) {
        const isIGST = !!N(it?.igstRate);
        if (isIGST) {
          grandTotal = round2(taxableAmount * (1 + rate / 100));
        } else {
          grandTotal = round2(
            taxableAmount * (1 + rate / 200 + rate / 200), // cgst+sgst halves
          );
        }
      }
      return {
        ...it,
        qty,
        price,
        totalPrice,
        taxableAmount,
        grandTotal,
      };
    });
  } else {
    r.orderItems = [];
  }

  // 6) Totals: prefer gstDetails; fall back to row totals if needed
  const cgstTotal = sumGST.cgst || N(r.cgstTotal);
  const sgstTotal = sumGST.sgst || N(r.sgstTotal);
  const igstTotal = sumGST.igst || N(r.igstTotal);

  r.cgstTotal = round2(cgstTotal);
  r.sgstTotal = round2(sgstTotal);
  r.igstTotal = round2(igstTotal);

  // 7) RoundOff and Grand Total – trust persisted values, but coerce to number
  r.roundOff = round2(N(r.roundOff));
  r.grandTotal = round2(N(r.grandTotal));

  // 8) coolieAndCartage / maxGstPercentage (optional; used by PDF component)
  if (r.coolieAndCartage === undefined) r.coolieAndCartage = 0;
  if (r.maxGstPercentage === undefined) {
    // try to infer maximum GST rate from items
    const maxRate = (r.orderItems || []).reduce(
      (m, it) => Math.max(m, N(it?.gstPercentage ?? it?.productId?.GSTRate)),
      0,
    );
    r.maxGstPercentage = maxRate;
  }

  // 9) igstTaxType normalization (0/1/true/false → boolean)
  r.igstTaxType = !!N(r.igstTaxType);

  return r;
}

/* ============== Amount-in-words ============== */
const toWords = new ToWords({
  localeCode: "en-IN",
  converterOptions: {
    currency: true,
    ignoreDecimal: false,
    ignoreZeroCurrency: false,
    doNotAddOnly: false,
    currencyOptions: {
      name: "Rupee",
      plural: "Rupees",
      symbol: "₹",
      fractionalUnit: { name: "Paisa", plural: "Paise", symbol: "" },
    },
  },
});

/* ============== Component ============== */
export default function CompleteOrder() {
  const [loading, setLoading] = useState(true);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [permissions, setPermissions] = useState({
    View: true,
    Create: true,
    Edit: true,
    Delete: true,
    Download: true,
  });

  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  // year/month filters
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");

  // column chooser
  const [modalCols, setModalCols] = useState(false);
  const addBufferRef = useRef([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  // invoice modals
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [showEwayInput, setShowEwayInput] = useState(false);
  const [ewayBillInput, setEwayBillInput] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // CN modal
  const [cnModal, setCnModal] = useState(false);
  const [cnForm, setCnForm] = useState({
    CNNumber: "",
    CNDate: "",
    CNQty: "",
    CNImage: null,
    CNImageBase64: "",
  });

  // email sending
  const [emailLoader, setEmailLoader] = useState(false);

  useEffect(() => injectOnce(), []);

  /* ===== columns ===== */
  const baseColumns = useMemo(
    () => [
      {
        key: "__actions__",
        header: "ACTIONS",
        get: () => "",
        render: (r) => {
          const checked = selectedIds.includes(r?._id);
          return (
            <div className="action-chips">
              <span
                className="chip chip-check"
                title={checked ? "Unselect" : "Select"}
                onClick={() => toggleCheck(r?._id)}
              >
                <input type="checkbox" checked={checked} readOnly />
              </span>

              {permissions?.Delete && (
                <span
                  className="chip chip-delete"
                  title="Delete"
                  onClick={() => deleteOne(r?._id)}
                >
                  <Trash2 size={14} color="white" />
                </span>
              )}
              {permissions?.Edit && (
                <span
                  className="chip chip-edit"
                  title="Edit"
                  onClick={() => editInvoice(r?._id)}
                >
                  <FaPencilAlt size={14} color="white" />
                </span>
              )}
            </div>
          );
        },
      },
      { key: "status", header: "STATUS", get: (r) => r?.status },
      {
        key: "invoice",
        header: "INVOICE",
        get: (r) => (r?.invoiceId === undefined ? "-" : "icon"),
        render: (r) =>
          r?.invoiceId === undefined ? (
            "-"
          ) : (
            <AiOutlineDownload
              title="Invoice Options"
              size={18}
              color="blue"
              onClick={() => handleInvoiceOptions(r)}
              style={{ cursor: "pointer" }}
            />
          ),
      },
      {
        key: "invoiceId",
        header: "INVOICE NO.",
        get: (r) => {
          if (r?.invoiceId === undefined) return "Total";
          const company = JSON.parse(
            localStorage.getItem("Companydetail") || "{}",
          );
          return `${company?.Prefix || ""}${r?.invoiceId || ""}${
            company?.Suffix || ""
          }`;
        },
      },
      { key: "date", header: "DATE", get: (r) => fmtDate(r?.date) },
      {
        key: "party",
        header: "PARTY NAME",
        get: (r) => r?.partyId?.CompanyName || r?.fullName,
      },
      {
        key: "limit",
        header: "PARTY LIMIT",
        get: (r) => r?.partyId?.limit || "",
      },
      { key: "amount", header: "AMOUNT", get: (r) => INR(r?.amount) },
      { key: "igstTotal", header: "IGST", get: (r) => INR(r?.igstTotal) },
      {
        key: "cgstTotal",
        header: "CGST",
        get: (r) => (typeof r?.cgstTotal === "number" ? INR(r.cgstTotal) : ""),
      },
      { key: "sgstTotal", header: "SGST", get: (r) => INR(N(r?.sgstTotal)) },
      { key: "roundOff", header: "ROUND OFF", get: (r) => INR(r?.roundOff) },
      {
        key: "grandTotal",
        header: "GRAND TOTAL",
        get: (r) => INR(r?.grandTotal),
      },
    ],
    [permissions, selectedIds],
  );

  // Column selection persistence
  const [selectedKeys, setSelectedKeys] = useState([]);
  useEffect(() => {
    const raw = localStorage.getItem("CompleteOrderList");
    let next = [];
    try {
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length) {
        const keys = new Set(["__actions__", ...baseColumns.map((c) => c.key)]);
        next = parsed.filter((k) => keys.has(k));
      }
    } catch {}
    if (!next.length) next = baseColumns.map((c) => c.key);
    setSelectedKeys(next);
  }, [baseColumns]);

  const colMap = useMemo(() => {
    const map = new Map();
    baseColumns.forEach((c) => map.set(c.key, c));
    return map;
  }, [baseColumns]);

  const visibleCols = useMemo(
    () => selectedKeys.map((k) => colMap.get(k)).filter(Boolean),
    [selectedKeys, colMap],
  );

  /* ===== fetch + filter ===== */
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const reload = async (yearInit, monthInit) => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}");

    // permissions
    const inside = CheckPermission("Complete Order");
    if (inside && typeof inside === "object") {
      setPermissions((p) => ({ ...p, ...inside }));
    }

    const current = new Date();
    const year = yearInit || current.getFullYear();
    const monthName = monthInit || monthNames[current.getMonth()];

    const res = await _Get(
      view_create_order_history,
      user?._id,
      user?.database,
    );

    let list = Array.isArray(res?.orderHistory) ? res.orderHistory : [];
    list = list.filter((x) => (x?.status || "").toLowerCase() !== "deactive");
    list = list.filter((x) => (x?.status || "").toLowerCase() === "completed");

    const filtered = list.filter((x) => {
      const d = new Date(x?.date);
      const y = d.getFullYear();
      const m = d.toLocaleString("default", { month: "long" });
      return y === Number(year) && m === monthName;
    });

    // date desc, then invoiceId desc
    filtered.sort((a, b) => {
      const da = new Date(a.date).getTime();
      const db = new Date(b.date).getTime();
      if (da === db) return Number(b.invoiceId || 0) - Number(a.invoiceId || 0);
      return db - da;
    });

    setRows(filtered);
    setSelectedYear(String(year));
    setSelectedMonth(monthName);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErrorMsg("");
      try {
        await reload();
      } catch (e) {
        console.error("[CompleteOrder] fetch error:", e);
        setErrorMsg(
          e?.response?.data?.message ||
            e?.message ||
            "Unable to load sales completed list.",
        );
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    const searchCols = visibleCols.filter(
      (c) => c.key !== "__actions__" && c.key !== "invoice",
    );
    return rows.filter((r) =>
      searchCols.some((c) => toStr(c.get(r)).toLowerCase().includes(term)),
    );
  }, [rows, search, visibleCols]);

  // totals row
  const totals = useMemo(() => {
    const sum = (key) =>
      filteredRows.reduce((acc, r) => acc + (Number(r?.[key]) || 0), 0);
    return {
      amount: sum("amount"),
      igstTotal: sum("igstTotal"),
      cgstTotal: sum("cgstTotal"),
      sgstTotal: sum("sgstTotal"),
      roundOff: sum("roundOff"),
      grandTotal: sum("grandTotal"),
    };
  }, [filteredRows]);

  /* ===== actions ===== */
  const toggleCheck = (id) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const deleteOne = (id) => {
    swal("Warning", "Sure you want to delete it?", {
      buttons: { cancel: "Cancel", catch: { text: "Delete", value: "delete" } },
    }).then((value) => {
      if (value === "delete") {
        _Delete(Delete_Sales, id)
          .then(() => {
            setRows((prev) => prev.filter((x) => x._id !== id));
            swal("Deleted", "Record has been deleted", "success");
          })
          .catch(() => {});
      }
    });
  };

  const deleteSelected = async () => {
    if (!selectedIds.length) return;
    const ok = await swal({
      title: "Are you sure?",
      text: "You are about to delete all selected items.",
      icon: "warning",
      buttons: ["Cancel", "Yes, delete all!"],
      dangerMode: true,
    });
    if (ok) {
      try {
        const res = await _DeleteAllSales("/order/delete-multiple", {
          sales: selectedIds,
        });
        if (res?.status) {
          await swal(
            "Deleted!",
            "Selected items have been deleted.",
            "success",
          );
          setRows((prev) => prev.filter((r) => !selectedIds.includes(r._id)));
          setSelectedIds([]);
        } else {
          await swal("Error!", "Something went wrong while deleting.", "error");
        }
      } catch (err) {
        await swal("Error!", "Something went wrong while deleting.", "error");
      }
    }
  };

  const editInvoice = (id) => {
    window.location = `/app/sales-invoices/edit/${id}`;
  };

  /* ===== invoice options flow ===== */
  const handleInvoiceOptions = async (row) => {
    setSelectedInvoice(row);
    const needEWay = N(row?.grandTotal) >= 50000;
    const hasARN = Array.isArray(row?.ARN)
      ? row.ARN.length > 0
      : !!toStr(row?.ARN);
    if (needEWay && !hasARN) {
      setShowEwayInput(true);
    } else {
      setShowOptionsModal(true);
    }
  };

  const submitEway = async () => {
    if (!ewayBillInput) {
      swal("Please enter E-Way Bill number");
      return;
    }
    try {
      await _PutUpdate(arnSave, selectedInvoice?._id, { ARN: ewayBillInput });
      swal("E-Way Bill saved");
      setRows((prev) =>
        prev.map((r) =>
          r._id === selectedInvoice?._id ? { ...r, ARN: ewayBillInput } : r,
        ),
      );
    } catch {
      swal("Error saving E-Way Bill");
    } finally {
      setShowEwayInput(false);
      setShowOptionsModal(true);
    }
  };

  /* ===== PDF generation (clean payload!) ===== */
  const generateInvoicePDF = async () => {
    try {
      const row = selectedInvoice;
      if (!row) return null;

      // Company details for prefix/suffix/logo etc.
      const company = JSON.parse(localStorage.getItem("Companydetail") || "{}");

      // Sanitize/normalize the print payload (SINGLE SOURCE OF TRUTH)
      const printPayload = buildPrintableInvoice(row, company);

      // Compute ledger snapshot (best-effort)
      let ledgerNumbers = {
        creditBalance: "0.00",
        debitBalance: "0.00",
        closingBalance: "0.00",
      };
      try {
        const res = await _Get(Last_Ledger_Balance, row?.partyId?._id);
        const led = res?.Ledger || {};
        const credit = N(led?.credit);
        const debit = N(led?.debit);
        ledgerNumbers = {
          creditBalance: credit.toFixed(2),
          debitBalance: debit.toFixed(2),
          closingBalance: (debit - credit).toFixed(2),
        };
      } catch {}

      const wordsNumber = toWords.convert(N(printPayload.grandTotal));

      // NOTE: We pass the SAME object to both props, to avoid mixing.
      const BilData = {
        CompanyDetails: company,
        PrintData: printPayload,
        wordsNumber,
      };

      const blob = await pdf(
        <LatestInvoice
          invoiceData={printPayload}
          CollectedData={ledgerNumbers}
          BilData={BilData}
          tableList={printPayload?.orderItems || []}
          AllCharges={printPayload?.discountDetails || []}
          fileName="invoice.pdf"
        />,
      ).toBlob();
      return blob;
    } catch (err) {
      console.error("PDF blob error:", err);
      return null;
    }
  };

  const downloadPdf = async () => {
    if (!selectedInvoice) return;
    const blob = await generateInvoicePDF();
    if (!blob) {
      swal("Failed to generate PDF.");
      return;
    }
    const company = JSON.parse(localStorage.getItem("Companydetail") || "{}");
    const fileNameId = `${company?.Prefix || ""}${
      selectedInvoice?.invoiceId || ""
    }${company?.Suffix || ""}`;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${
      selectedInvoice?.partyId?.CompanyName ||
      selectedInvoice?.fullName ||
      "invoice"
    }-${fileNameId}.pdf`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const sendInvoiceEmail = async () => {
    if (!selectedInvoice) return;
    setEmailLoader(true);
    try {
      const blob = await generateInvoicePDF();
      if (!blob) throw new Error("PDF gen failed");

      const fd = new FormData();
      const email =
        selectedInvoice?.partyId?.email || selectedInvoice?.email || "";
      const rawDate = selectedInvoice?.date;
      const formattedDate = rawDate
        ? new Date(rawDate).toLocaleDateString("en-GB")
        : "";

      fd.append("invoice", blob, "invoice.pdf");
      fd.append("customer", email);
      fd.append("title", "This is a computer-generated invoice");
      fd.append("date", formattedDate);
      fd.append("total", N(selectedInvoice?.grandTotal));
      fd.append("invoiceId", toStr(selectedInvoice?.invoiceId));
      fd.append(
        "partyName",
        selectedInvoice?.partyId?.CompanyName ||
          selectedInvoice?.fullName ||
          "",
      );
      fd.append(
        "address",
        selectedInvoice?.partyId?.ownerAddress ||
          selectedInvoice?.ownerAddress ||
          selectedInvoice?.address ||
          "",
      );
      fd.append("CNDate", selectedInvoice?.CNDate || "--");
      fd.append("CNNumber", selectedInvoice?.CNNumber || "--");
      fd.append("CNQty", selectedInvoice?.CNQty || "--");

      // company credentials
      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      fd.append("superAdminName", user?.companyName || "");
      fd.append("email", user?.email || "");
      fd.append("appPassword", user?.appPassword || "");

      await axios.post(`${baseURL}/order/send-invoice`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      swal("Invoice sent successfully!");
    } catch (e) {
      console.error(e);
      swal("Error sending invoice");
    } finally {
      setEmailLoader(false);
    }
  };

  // CN update
  const submitCN = async () => {
    try {
      const form = new FormData();
      form.append("CNNumber", cnForm.CNNumber || "");
      form.append("CNDate", cnForm.CNDate || "");
      form.append("CNQty", cnForm.CNQty || "");
      if (cnForm.CNImage) form.append("CNImage", cnForm.CNImage);
      await _Put(updatedCNData, selectedInvoice?._id, form);
      swal("CN Data updated");
      setCnModal(false);
      setShowOptionsModal(false);
    } catch (e) {
      console.error(e);
      setShowOptionsModal(false);
    }
  };

  /* ===== export ===== */
  const handleExportPDF = async () => {
    const cols = visibleCols;
    const header = cols.map((c) => c.header).join(",");
    const lines = filteredRows.map((r) =>
      cols
        .map((c) => {
          const v = toStr(
            typeof c.render === "function" ? c.get(r) : c.get(r),
          ).replace(/"/g, '""');
          return `"${v}"`;
        })
        .join(","),
    );
    const csv = [header, ...lines].join("\r\n");
    await exportDataToPDF(csv, "CompletedOrderList");
  };

  const handleExportXLSX = async () => {
    const cols = visibleCols;
    const data = filteredRows.map((r) => {
      const obj = {};
      cols.forEach((c) => (obj[c.header] = toStr(c.get(r))));
      return obj;
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      "CompletedOrderList.xlsx",
    );
  };

  /* ===== column chooser ===== */
  const openCols = () => {
    addBufferRef.current = [];
    setSelectedIndex(-1);
    setModalCols(true);
  };
  const closeCols = () => setModalCols(false);

  const handleCheckbox = (checked, colKey) => {
    const buf = addBufferRef.current.slice();
    if (checked) {
      if (!buf.includes(colKey)) buf.push(colKey);
    } else {
      const i = buf.indexOf(colKey);
      if (i >= 0) buf.splice(i, 1);
    }
    addBufferRef.current = buf;
  };

  const addSelectedColumns = () => {
    const next = Array.from(
      new Set([...selectedKeys, ...addBufferRef.current]),
    );
    setSelectedKeys(next);
  };

  const removeAtIndex = () => {
    if (selectedIndex < 0) return;
    const next = selectedKeys.slice();
    next.splice(selectedIndex, 1);
    setSelectedKeys(next);
    setSelectedIndex(-1);
  };
  const shiftUp = () => {
    if (selectedIndex <= 0) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex - 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex - 1);
  };
  const shiftDown = () => {
    if (selectedIndex < 0 || selectedIndex >= selectedKeys.length - 1) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex + 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex + 1);
  };
  const applyColumns = (e) => {
    e.preventDefault();
    localStorage.setItem("CompleteOrderList", JSON.stringify(selectedKeys));
    closeCols();
  };

  /* ===== render ===== */
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  const noData = !filteredRows?.length;

  return (
    <>
      {errorMsg && (
        <Row>
          <Col sm="12">
            <Alert color="danger" style={{ marginBottom: 10 }}>
              {errorMsg}
            </Alert>
          </Col>
        </Row>
      )}

      {/* === Toolbar === */}
      <Row className="co-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            style={{
              paddingTop: 4,
              paddingBottom: 4,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
              background: "#f4f7f6",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Sales Completed List{" "}
                <span className="count-pill" title="records">
                  {filteredRows.length}
                </span>
              </div>

              <div style={{ flex: "1 1 180px" }} />

              {/* Search */}
              <div style={{ minWidth: 240, display: "flex", gap: 8 }}>
                <Input
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ background: "#fff" }}
                />
              </div>

              {/* Year / Month / Submit */}
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <Input
                  type="select"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  style={{ minWidth: 120, background: "#fff" }}
                >
                  <option value="">Year</option>
                  {Array.from({ length: 5 }, (_, i) => {
                    const y = new Date().getFullYear() - i;
                    return (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    );
                  })}
                </Input>
                <Input
                  type="select"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  style={{ minWidth: 140, background: "#fff" }}
                >
                  <option value="">Month</option>
                  {monthNames.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </Input>
                <Button
                  color="primary"
                  style={{ height: 32 }}
                  onClick={() =>
                    reload(
                      selectedYear || undefined,
                      selectedMonth || undefined,
                    )
                  }
                  disabled={!selectedYear || !selectedMonth}
                >
                  Submit
                </Button>
              </div>

              {/* Column chooser */}
              <Button
                outline
                color="primary"
                onClick={openCols}
                title="Filter Columns"
                style={{
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaFilter size={14} />
              </Button>

              {/* Bulk delete */}
              {selectedIds.length > 0 && (
                <Button
                  color="danger"
                  style={{ height: 32 }}
                  onClick={deleteSelected}
                >
                  Delete All
                </Button>
              )}

              {/* Export */}
              {permissions?.Download && (
                <div
                  onMouseEnter={() => setDownloadOpen(true)}
                  onMouseLeave={() => setDownloadOpen(false)}
                  style={{ position: "relative" }}
                >
                  <ImDownload
                    style={{ cursor: "pointer" }}
                    title="download file"
                    size={24}
                    color="rgb(8, 91, 245)"
                  />
                  {downloadOpen && (
                    <div
                      style={{
                        position: "absolute",
                        right: 0,
                        top: 32,
                        zIndex: 5,
                        border: "1px solid rgb(8, 91, 245)",
                        backgroundColor: "white",
                        borderRadius: 10,
                        overflow: "hidden",
                        minWidth: 130,
                        boxShadow: "0 12px 24px rgba(0,0,0,.12)",
                      }}
                    >
                      <div onClick={handleExportPDF} style={dlItemStyle}>
                        .PDF
                      </div>
                      <div onClick={handleExportXLSX} style={dlItemStyle}>
                        .XLSX
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* === Table === */}
      <Row>
        <Col sm="12">
          <Card
            style={{
              padding: 8,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
            }}
          >
            {noData ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                No data found.
              </div>
            ) : (
              <div className="co-table-wrap">
                <table className="co-table">
                  <thead>
                    <tr>
                      {visibleCols.map((c) => (
                        <th key={c.key}>{c.header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((r) => (
                      <tr key={r?._id}>
                        {visibleCols.map((c) => (
                          <td key={c.key}>
                            {typeof c.render === "function"
                              ? c.render(r)
                              : toStr(c.get(r))}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {/* sticky totals row */}
                    <tr className="co-totals">
                      {visibleCols.map((c) => {
                        if (c.key === "party")
                          return (
                            <td key={c.key} style={{ textAlign: "right" }}>
                              Total:
                            </td>
                          );
                        if (
                          [
                            "amount",
                            "igstTotal",
                            "cgstTotal",
                            "sgstTotal",
                            "roundOff",
                            "grandTotal",
                          ].includes(c.key)
                        ) {
                          const val = totals[c.key] || 0;
                          return <td key={c.key}>{INR(val)}</td>;
                        }
                        return <td key={c.key} />;
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Column Chooser Modal */}
      <Modal
        isOpen={modalCols}
        toggle={closeCols}
        zIndex={2000}
        backdrop="static"
        keyboard={false}
        style={{ maxWidth: 1050 }}
      >
        <ModalHeader toggle={closeCols}>Change Fields</ModalHeader>
        <ModalBody>
          <Row>
            <Col lg="4" md="4" sm="12" xl="4" xs="12">
              <h5>Available Columns</h5>
              <div style={listBoxStyle}>
                {baseColumns.map((col) => (
                  <div key={col.key} style={pickItemStyle}>
                    <label
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        type="checkbox"
                        onChange={(e) =>
                          handleCheckbox(e.target.checked, col.key)
                        }
                      />
                      <span>{col.header}</span>
                    </label>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <Button size="sm" color="primary" onClick={addSelectedColumns}>
                  Add Selected →
                </Button>
              </div>
            </Col>

            <Col lg="8" md="8" sm="12" xl="8" xs="12">
              <h5>Visible Columns</h5>
              <div
                style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
              >
                <div style={{ ...listBoxStyle, flex: 1 }}>
                  {selectedKeys.map((key, i) => {
                    const col = colMap.get(key);
                    if (!col) return null;
                    return (
                      <div
                        key={key}
                        style={{
                          ...pickItemStyle,
                          background: i === selectedIndex ? "#1877f2" : "#fff",
                          color: i === selectedIndex ? "#fff" : "#000",
                          cursor: "pointer",
                        }}
                        onClick={() => setSelectedIndex(i)}
                      >
                        {col.header}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  <Button size="sm" onClick={shiftUp}>
                    ↑
                  </Button>
                  <Button size="sm" onClick={shiftDown}>
                    ↓
                  </Button>
                  <Button size="sm" color="danger" onClick={removeAtIndex}>
                    Remove
                  </Button>
                </div>
              </div>
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Button color="primary" onClick={applyColumns}>
                  Submit
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>

      {/* E-Way input modal */}
      <Modal isOpen={showEwayInput} toggle={() => setShowEwayInput(false)}>
        <ModalHeader toggle={() => setShowEwayInput(false)}>
          Enter E-Way Bill Number
        </ModalHeader>
        <ModalBody>
          <Label>E-Way Bill Number</Label>
          <Input
            value={ewayBillInput}
            onChange={(e) => setEwayBillInput(e.target.value)}
            placeholder="Enter E-Way Bill Number"
          />
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={submitEway}>
            Submit
          </Button>
          <Button color="secondary" onClick={() => setShowEwayInput(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Options modal */}
      <Modal
        isOpen={showOptionsModal}
        toggle={() => setShowOptionsModal(false)}
      >
        <ModalHeader toggle={() => setShowOptionsModal(false)}>
          Choose Action
        </ModalHeader>
        <ModalBody>What would you like to do with this invoice?</ModalBody>
        <ModalFooter>
          <Button color="success" onClick={downloadPdf}>
            Download
          </Button>
          <Button color="success" onClick={() => setCnModal(true)}>
            Update CN Data
          </Button>
          <Button
            color="info"
            onClick={sendInvoiceEmail}
            disabled={emailLoader}
          >
            {emailLoader ? (
              <Spinner size="sm" style={{ color: "white" }} />
            ) : (
              "Send to Email"
            )}
          </Button>
          <Button color="secondary" onClick={() => setShowOptionsModal(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* CN Modal */}
      <Modal isOpen={cnModal} toggle={() => setCnModal(false)}>
        <ModalHeader toggle={() => setCnModal(false)}>
          Update CN Data
        </ModalHeader>
        <ModalBody>
          <Form className="co-form">
            <FormGroup>
              <Label for="CNImage">CN Image</Label>
              <Input
                type="file"
                id="CNImage"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onloadend = () =>
                    setCnForm((p) => ({
                      ...p,
                      CNImage: file,
                      CNImageBase64: reader.result,
                    }));
                  reader.readAsDataURL(file);
                }}
              />
            </FormGroup>
            <FormGroup>
              <Label for="CNNumber">CN Number</Label>
              <Input
                type="text"
                id="CNNumber"
                value={cnForm.CNNumber}
                onChange={(e) =>
                  setCnForm((p) => ({ ...p, CNNumber: e.target.value }))
                }
              />
            </FormGroup>
            <FormGroup>
              <Label for="CNDate">CN Date</Label>
              <Input
                type="date"
                id="CNDate"
                value={cnForm.CNDate}
                onChange={(e) =>
                  setCnForm((p) => ({ ...p, CNDate: e.target.value }))
                }
              />
            </FormGroup>
            <FormGroup>
              <Label for="CNQty">CN Quantity</Label>
              <Input
                type="number"
                id="CNQty"
                value={cnForm.CNQty}
                onChange={(e) =>
                  setCnForm((p) => ({ ...p, CNQty: e.target.value }))
                }
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="success" onClick={submitCN}>
            Submit
          </Button>
          <Button color="secondary" onClick={() => setCnModal(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

/* little styles */
const dlItemStyle = {
  padding: "10px 12px",
  cursor: "pointer",
  fontWeight: 600,
  letterSpacing: ".02em",
};
const listBoxStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  maxHeight: 280,
  overflow: "auto",
  background: "#fff",
  padding: 8,
};
const pickItemStyle = {
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid #e5e7eb",
  marginBottom: 6,
  background: "#fff",
};
